import { useLocation } from '@reach/router';
import Heading from '@vfuk/core-heading';
import Icon from '@vfuk/core-icon';
import Paragraph from '@vfuk/core-paragraph';
import axios from 'axios';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReadLocalStorage } from 'usehooks-ts';

import PricingCardCarousel from '@/components/pageBuilder/Sections/PricingCardCarousel';
import { TPricingCardCarousel } from '@/components/pageBuilder/Sections/PricingCardCarousel/PricingCardCarousel.types';
import SWAvailableAppsCarouselIcons from '@/components/pageBuilder/Sections/SWAvailableAppsCarouselIcons';
import isEnvDevelopment from '@/helpers/isEnvDevelopment';
import { useModalContext } from '@/hooks/modal/useModal';
import { TError } from '@/services/error.types';
import { retrieveProduct } from '@/services/product.services';

import SWSectionFiftyFiftyBanner from '../SWSectionFiftyFiftyBanner/SWSectionFiftyFiftyBanner';
import { Icons, IconWithText, PricingContentWrapper, Title } from './SWPricingContent.styles';
import { SWPricingContentProps } from './SWPricingContent.types';

const SWPricingContent: FC<SWPricingContentProps> = ({
  id,
  contentful_id,
  name,
  buttonsAction,
  buttonsAppearance,
  backgroundColor,
  backgroundImage,
  iconsWithTitle,
  includesCards,
  products,
  banners,
  bottomLink,
  subHeading,
  bottomLinkUrl,
  availableApplications,
}) => {
  const [productsWithDxlInfo, setProductsWithDxlInfo] = useState<TPricingCardCarousel['products']>([]);
  const stackableProduct = useReadLocalStorage<boolean>('vf_stackable');
  const { pathname } = useLocation();
  const BusinessBoostPage = pathname.includes('business-boost');
  const { setErrorInfo, toggleModal } = useModalContext();
  const { t } = useTranslation();

  useEffect(() => {
    try {
      const productsIds = products.map(product => product.productId);
      const productsWithEditions = products.map(async product => {
        product.dxlInformation = undefined;
        if (productsIds.includes(product.productId)) {
          const newProduct = await retrieveProduct(product.productId);
          if (!axios.isAxiosError(newProduct) && newProduct.status === 200 && newProduct.data && newProduct.data.id) {
            product.dxlInformation = newProduct.data.dxlInformation;
          } else {
            setErrorInfo({
              ...(axios.isAxiosError(newProduct) && {
                statusType: newProduct.response?.data?.error,
                statusCode: newProduct.response?.data?.code,
              }),
              requestUrl: 'catalogues',
            });
            toggleModal(true, 'Error');
          }
          return product;
        }
      });
      setProductsWithDxlInfo(productsWithEditions);
    } catch (err) {
      const error = err as TError;
      isEnvDevelopment() && console.log(error?.response?.data.error);
    }
  }, []);

  return (
    <>
      <PricingContentWrapper
        backgroundColor={backgroundColor}
        backgroundImage={backgroundImage}
        productsLength={products?.length || 0}
      >
        {name && <Title>{name}</Title>}
        <PricingCardCarousel
          id={id}
          contentful_id={contentful_id}
          name={name}
          buttonsAction={buttonsAction}
          buttonsAppearance={buttonsAppearance}
          products={productsWithDxlInfo}
          stackableProduct={stackableProduct}
          bottomLink={bottomLink}
          subHeading={subHeading}
          bottomLinkUrl={bottomLinkUrl}
          includesCards={includesCards}
        />
        {iconsWithTitle && iconsWithTitle.length > 0 && (
          <Icons>
            {iconsWithTitle.map(({ id, iconName, name }) => (
              <IconWithText key={id}>
                <Icon name={iconName} group="system" />
                <Heading>{name}</Heading>
              </IconWithText>
            ))}
          </Icons>
        )}

        {!stackableProduct && buttonsAction !== 'configureYourPack' && products?.length > 1 && !BusinessBoostPage && (
          <Paragraph>{t('pricingRedSentence')}</Paragraph>
        )}
      </PricingContentWrapper>
      {availableApplications && <SWAvailableAppsCarouselIcons {...availableApplications} />}
      {banners && banners.map(banner => <SWSectionFiftyFiftyBanner key={banner.id} {...banner} />)}
    </>
  );
};

export default SWPricingContent;
